import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

export function FirstLevelSelector({
  analysis,
  selectedFirstLevels,
  completedExtractions,
  selectedCountry,
  onToggle,
  onSelectAll,
  onSelectPending,
  onClear,
  onProceed,
}) {
  const isExtracted = (name) =>
    completedExtractions.some(
      (c) => c.country === selectedCountry && c.first_level === name
    );

  const getBeachCount = (name) => {
    const found = completedExtractions.find(
      (c) => c.country === selectedCountry && c.first_level === name
    );
    return found ? found.beaches_count : 0;
  };

  const subdivisions = analysis.coastal_subdivisions || [];
  const selectedCount = selectedFirstLevels.length;

  const proceedLabel =
    selectedCount === 1 && selectedFirstLevels[0].needs_drilldown
      ? `Drill Down into ${selectedFirstLevels[0].name}`
      : `Extract ${selectedCount} Region${selectedCount > 1 ? "s" : ""}`;

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="first-level-selection">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Step 3: Select Coastal {analysis.subdivision_type}s</CardTitle>
            <CardDescription>Select one or more to extract (green = already done)</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button data-testid="btn-select-all-pending" variant="outline" size="sm" onClick={onSelectPending} className="border-slate-700 text-xs">Select Pending</Button>
            <Button data-testid="btn-select-all-first" variant="outline" size="sm" onClick={onSelectAll} className="border-slate-700 text-xs">Select All</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-72">
          <div className="grid grid-cols-3 gap-2">
            {subdivisions.map((sub, i) => {
              const name = sub.name || sub;
              const size = sub.size || "medium";
              const needsDrill = sub.needs_drilldown;
              const extracted = isExtracted(name);
              const beachCount = getBeachCount(name);
              const selected = selectedFirstLevels.some((s) => (s.name || s) === name);

              let cardClass = "bg-slate-800/50 hover:bg-slate-700/50 border border-transparent hover:border-slate-600";
              if (selected) {
                cardClass = "bg-cyan-500/20 border border-cyan-500/50";
              } else if (extracted) {
                cardClass = "bg-emerald-500/10 border border-emerald-500/30 hover:border-emerald-500/50";
              }

              return (
                <div
                  key={`fl-${name}`}
                  data-testid={`first-level-item-${i}`}
                  onClick={() => onToggle(sub)}
                  className={`flex items-center gap-2 p-3 rounded cursor-pointer transition-all ${cardClass}`}
                >
                  <Checkbox checked={selected} className="pointer-events-none shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {extracted && (
                        <svg className="w-3.5 h-3.5 text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      <span className={`font-medium truncate ${extracted ? "text-emerald-300" : ""}`}>{name}</span>
                    </div>
                    {extracted && (
                      <div className="text-[10px] text-emerald-500/70 mt-0.5">{beachCount.toLocaleString()} beaches</div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Badge variant="outline" className="text-xs">{size}</Badge>
                    {needsDrill && <Badge className="text-xs bg-amber-500/20 text-amber-400">drill</Badge>}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
        {selectedCount > 0 && (
          <div className="mt-4 flex items-center justify-between border-t border-slate-800 pt-4">
            <div className="text-sm text-slate-400">
              {selectedCount} of {subdivisions.length} selected
            </div>
            <div className="flex gap-2">
              <Button data-testid="btn-clear-first" variant="outline" size="sm" onClick={onClear} className="border-slate-700">Clear</Button>
              <Button data-testid="btn-proceed-first" onClick={onProceed} className="bg-cyan-600 hover:bg-cyan-500">
                {proceedLabel}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
